import { PropTypes } from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import injectClickOutside from 'react-click-outside';
import styled, { withTheme } from 'styled-components';

import { Arrow as UnstyledArrow } from '../../../icons';
import { Link } from '../../../link';
import { FontFamily } from '../../../typography';
import { USER_TYPE } from '../../constants';
import { DesktopUserLinks, DesktopPatientWithNavigationLinks } from '../constants';

class UsernameDropdownBase extends React.PureComponent {
    static propTypes = {
        userType: PropTypes.oneOf(Object.values(USER_TYPE)).isRequired,
        displayName: PropTypes.string,
        onUsernameBarClick: PropTypes.func,
        onUsernameDropdownLinkClick: PropTypes.func,
        defaultOpen: PropTypes.bool,
        usePatientNavigationLinks: PropTypes.bool,
    }

    constructor(props) {
        super(props);

        this.state = {
            dropdownOpen: this.props.defaultOpen,
        };
    }

    render() {
        const {
            dropdownOpen,
        } = this.state;

        const {
            theme,
            displayName,
        } = this.props;

        const textColor = dropdownOpen ?
            theme.header.usernameDropdown.labelActiveTextColor :
            theme.header.usernameDropdown.labelTextColor;
        const colors = {
            backgroundColor: dropdownOpen ?
                theme.header.usernameDropdown.labelActiveBgColor :
                theme.header.usernameDropdown.labelBgColor,
            color: textColor,
        };
        const arrowDirection = dropdownOpen ? 'up' : 'down';
        return (
            <Wrapper>
                <DropdownContainer data-test="user-dropdown" >
                    <UsernameBar
                        data-test="username-header"
                        onClick={this.toggleAction}
                        style={colors}
                    >
                        <Username
                            className="exclude-from-playback"
                            data-test="username"
                        >
                            {displayName}
                        </Username>
                        <StyledArrow
                            color={textColor}
                            direction={arrowDirection}
                            height={8}
                        />
                    </UsernameBar>
                    {this.maybeRenderDropdownMenu()}
                </DropdownContainer>
            </Wrapper>
        );
    }

    renderDropdownLinks(links) {
        return (
            <DropdownMenu>
                {links.map(link => {
                    return this.renderDropdownBar(link);
                })}
            </DropdownMenu>
        );
    }

    maybeRenderDropdownMenu() {
        const { userType } = this.props;
        if (this.state.dropdownOpen) {
            const links = this.getLinks(userType);
            return this.renderDropdownLinks(links);
        }
    }

    renderDropdownBar(link) {
        const dropdownIcon = link.icon ? (
            <Icon>
                <link.icon height={link.iconHeight || 20} />
            </Icon>
        ) : null;

        return (
            <DropdownBar
                key={link.dataTest}
                beforeClick={this.props.onUsernameDropdownLinkClick}
                data-test={`username-dropdown-${link.dataTest}`}
                href={link.url}
            >
                {dropdownIcon}
                <DropdownText>
                    <FormattedMessage {...link.message} />
                </DropdownText>
            </DropdownBar>
        );
    }

    toggleAction = () => {
        const { onUsernameBarClick } = this.props;
        const dropdownToggled = !this.state.dropdownOpen;

        if (dropdownToggled && onUsernameBarClick) {
            onUsernameBarClick();
        }

        this.setState({ dropdownOpen: dropdownToggled });
    }

    handleClickOutside = () => {
        this.setState({ dropdownOpen: false });
    };

    getLinks(userType) {
        const {
            usePatientNavigationLinks,
        } = this.props;
        switch (userType) {
            case USER_TYPE.Patient:
                return usePatientNavigationLinks ? DesktopPatientWithNavigationLinks : DesktopUserLinks.Patient;
            case USER_TYPE.Provider:
                return DesktopUserLinks.Provider;
            case USER_TYPE.ZdEmployee:
                return DesktopUserLinks.ZdEmployee;
        }
    }
}

const Wrapper = styled.div`
    display: inline-block;
    min-width: 240px;
    vertical-align: top;
`;

const DropdownContainer = styled.div`
    width: 100%;
    position: relative;
`;

const UsernameBar = styled.div`
    display: flex;
    justify-content: flex-start;
    height: 40px;
    width: 100%;

    border-style: solid;
    cursor: pointer;
    vertical-align: middle;
`;

const DropdownBar = styled(Link)`
    align-items: center;
    background-color: ${p => p.theme.header.usernameDropdown.itemBgColor};
    border: none !important;
    color: ${p => p.theme.header.usernameDropdown.itemTextColor};
    display: flex;
    height: 40px;
    width: 100%;

    &:hover {
        background-color: ${p => p.theme.header.usernameDropdown.itemHoverBgColor};
        cursor: pointer;
    }

    &:active {
        background-color: ${p => p.theme.header.usernameDropdown.itemTextColor};
        color: ${p => p.theme.header.usernameDropdown.itemBgColor};

        svg path {
            fill: ${p => p.theme.header.usernameDropdown.itemBgColor};
        }

        svg polygon {
            fill: ${p => p.theme.header.usernameDropdown.itemBgColor};
        }
    }
`;

const Icon = styled.div`
    align-items: center;
    display: inline-flex;
    height: 30px;
    justify-content: center;
    margin-left: 16px;
    width: 30px;
`;

const StyledArrow = styled(UnstyledArrow)`
    margin-left: auto;
    margin-right: 19px;
    margin-top: 17px;
`;


const DropdownText = styled.div`
    display: inline-block;
    margin-left: 16px;
    font: 15px/24px ${FontFamily.SharpSansSemibold};
`;

const Username = styled.div`
    display: inline-block;
    padding-top: 8px;
    margin-left: 16px;
    margin-right: 19px;
    font: 15px/24px ${FontFamily.SharpSansSemibold};
`;

const DropdownMenu = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    z-index: 12;
`;

export const UsernameDropdown = withTheme(injectClickOutside(UsernameDropdownBase));
